import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Truck, ClipboardList, Star,
  Navigation, CheckCircle, DollarSign, Activity,
  Users,
} from "lucide-react";
import API from "../../api/axiosConfig";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");


function StatCard({ icon: Icon, label, value, sub, accent, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4"
    >
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${accent}`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </motion.div>
  );
}

export default function DeliveryDashboard() {
  const [company,      setCompany]      = useState(null);
  const [ryders,       setRyders]       = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    API.get("/delivery-companies/me")
      .then(r => {
        const comp = r.data?.data?.company ?? r.data?.company;
        setCompany(comp);
        if (!comp?.id) return;
        return Promise.all([
          API.get(`/delivery-companies/${comp.id}/ryders`),
          API.get(`/delivery-companies/${comp.id}/applications`),
        ]).then(([rRes, aRes]) => {
          setRyders(rRes.data?.data?.ryders ?? []);
          setApplications(aRes.data?.data?.applications ?? []);
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const disponibles    = ryders.filter(r => r.status === "disponible").length;
  const ocupados       = ryders.filter(r => r.status === "ocupado").length;
  const pendingApps    = applications.filter(a => a.status === "pendiente" && a.type === "application").length;
  const pendingInvites = applications.filter(a => a.status === "pendiente" && a.type === "invitation").length;
  const totalDeliveriesToday = ryders.reduce((s, r) => s + (r.deliveriesToday ?? 0), 0);
  const totalEarningsToday   = ryders.reduce((s, r) => s + (r.earningsToday   ?? 0), 0);
  const avgRating = ryders.length
    ? (ryders.reduce((s, r) => s + Number(r.rating ?? 0), 0) / ryders.length).toFixed(1)
    : "—";

  if (loading) {
    return (
      <div className="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array(8).fill(0).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl h-24 animate-pulse border border-gray-100" />
        ))}
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black text-gray-900">Dashboard</h1>
        <p className="text-xs text-gray-500 mt-0.5">{company?.name ?? "Tu empresa de domicilio"} · {dayjs().format("dddd D [de] MMMM")}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Truck}       label="Ryders totales"     value={ryders.length}           accent="bg-indigo-500"  delay={0}    />
        <StatCard icon={Activity}    label="Disponibles"        value={disponibles}              accent="bg-emerald-500" delay={0.05} />
        <StatCard icon={Navigation}  label="En servicio"        value={ocupados}                 accent="bg-amber-500"   delay={0.1}  />
        <StatCard icon={ClipboardList} label="Solicitudes pend." value={pendingApps}             accent="bg-red-500"     delay={0.15} />
        <StatCard icon={CheckCircle} label="Entregas hoy"       value={totalDeliveriesToday}     accent="bg-violet-500"  delay={0.2}  />
        <StatCard icon={DollarSign}  label="Ingresos hoy"       value={`$${totalEarningsToday.toLocaleString()}`} accent="bg-teal-500" delay={0.25} />
        <StatCard icon={Star}        label="Rating promedio"    value={avgRating}                accent="bg-orange-500"  delay={0.3}  />
        <StatCard icon={Users}       label="Invitaciones env."  value={pendingInvites}           accent="bg-pink-500"    delay={0.35} />
      </div>

      {/* Ryders status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Active ryders */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Truck size={15} className="text-indigo-600" />
            <h2 className="font-bold text-gray-900 text-sm">Ryders activos</h2>
            <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">{disponibles + ocupados}</span>
          </div>
          <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
            {ryders.filter(r => r.status !== "offline").length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">Sin ryders activos ahora</p>
            ) : (
              ryders.filter(r => r.status !== "offline").map(r => (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(r.name || r.user?.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{r.name || r.user?.name}</p>
                    <p className="text-xs text-gray-500">{r.deliveriesToday ?? 0} entregas · {Number(r.rating ?? 0).toFixed(1)}★</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                    r.status === "disponible" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}>
                    {r.status === "disponible" ? "Disponible" : "En servicio"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending applications */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <ClipboardList size={15} className="text-red-500" />
            <h2 className="font-bold text-gray-900 text-sm">Solicitudes recientes</h2>
            {pendingApps > 0 && (
              <span className="ml-auto text-xs bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{pendingApps} pendientes</span>
            )}
          </div>
          <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
            {applications.filter(a => a.type === "application").length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">Sin solicitudes recibidas</p>
            ) : (
              applications.filter(a => a.type === "application").slice(0, 8).map(a => (
                <div key={a.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(a.ryder?.user?.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{a.ryder?.user?.name}</p>
                    <p className="text-xs text-gray-500">{a.ryder?.vehicleType} · {dayjs(a.createdAt).fromNow()}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                    a.status === "pendiente"  ? "bg-amber-100 text-amber-700"
                    : a.status === "aprobado" ? "bg-emerald-100 text-emerald-700"
                    : "bg-red-100 text-red-700"
                  }`}>
                    {a.status === "pendiente" ? "Pendiente" : a.status === "aprobado" ? "Aprobado" : "Rechazado"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
